import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { useAuthContext } from "../auth/AuthContext";
import { getStoredTokensVersion, subscribeStoredTokens } from "../auth/b2cClient";
import { jwtUserOid } from "../auth/jwt";
import { PpLoginGate } from "../auth/ppLoginGate";
import {
  ensurePpAccessToken,
  getPpToken,
  hasPersistedPpLink,
  resolveIdentityEmail,
} from "../auth/ppToken";
import { EntitledIpasError, fetchEntitledIpas, type EntitledIpaEntry } from "./fetchEntitledIpas";
import { clearStoredIpa, getStoredIpa, setStoredIpa } from "./ipaStorage";
import { clearStoredNpi, getStoredNpi, isValidNpi, setStoredNpi } from "./npiStorage";

/**
 * Patient Priority Index worklist access gate (P6c SHIP-GATE resolution, plus
 * the auto-link-NPI follow-up).
 *
 * A provider may only view the worklist when ALL of the following are true:
 *   1. They hold a Provider Portal bearer (the `ProviderOverview/
 *      PatientPriorityIndex` endpoint is PP-scoped) — reuses the EXISTING
 *      "seamless PP login" mechanism (auth/ppToken.ts's `getPpToken`/
 *      `hasPersistedPpLink`/`ensurePpAccessToken`, auth/PpLoginModal.tsx,
 *      auth/ppLoginGate.ts) that MemberProfilePage's Create-Auth flow already
 *      uses for SSO/Pathways users with no PP bearer. NOT rebuilt here.
 *   2. Their account resolves to at least one IPA. AstraConnect can't derive
 *      this locally (member_360 CONFIG has no per-user IPA storage and
 *      care-management-gql leaves `permissionMeta.ipas` undefined), so the IPA
 *      set is fetched from the provider's Provider Portal identity via
 *      `fetchEntitledIpas` (POST ProviderOverview/EntitledIpas) — the same
 *      bearer as (1). Zero IPAs is an explicit error state (never silently send
 *      companyId=""). One or more IPAs resolve WITHOUT asking the provider:
 *      the per-device pick (keyed by oid, see ipaStorage.ts) if it's still
 *      entitled, else the first entitled IPA. A failed fetch is its own
 *      retryable error state — never collapsed into "no IPAs".
 *   3. They have an NPI resolved for that IPA. The SAME `EntitledIpas`
 *      response carries each IPA's entitled provider NPI(s)
 *      (`EntitledIpaEntry.providerNpis`), so the NPI is resolved from that
 *      list — after the IPA has resolved (an IPA is a precondition for even
 *      knowing which NPI list applies). Exactly one entitled NPI is
 *      auto-linked (never persisted — the server is authoritative on every
 *      load); more than one resolves to the previously-picked value if it's
 *      still in the current list, else the first entitled NPI; zero entitled
 *      NPIs falls back to the original provider-supplied manual entry,
 *      persisted per-device keyed by (oid, ipa) (see npiStorage.ts /
 *      `resolveWorklistNpi`).
 *
 * ACT-1431 — NEITHER (2) NOR (3) BLOCKS THE SURFACE any more. Both used to
 * interrupt a multi-IPA / multi-NPI provider with a full-page picker; both now
 * auto-resolve and expose the choice as a non-blocking switcher in the
 * worklist's identity bar (WorklistGateStates.tsx's `WorklistIdentityBar`).
 * This matches Provider Portal, which removed its own IPA picker outright in
 * AM-6302 (`frontend/src/utils/ipaContext.ts`'s `resolveIpa`) and auto-picks
 * the first/persisted provider for the NPI. The fail-closed states
 * (`no-pp-session`, `ipa-forbidden`, `ipa-error`, `no-ipa`, `no-npi`) are
 * untouched — auto-resolving a CHOICE is not the same as relaxing a DENIAL.
 */
export type WorklistAccessStatus =
  | "no-pp-session"
  | "no-npi"
  | "resolving-ipa"
  | "ipa-error"
  | "ipa-forbidden"
  | "no-ipa"
  | "ready";

/**
 * Lifecycle of the async IPA fetch from PP-API. `"forbidden"` is a distinct
 * terminal state from `"error"`: it means PP-API's EntitledIpas call returned
 * a 403 — a fail-closed BACKEND DENIAL (the signed-in account is
 * authenticated but not entitled to a worklist), not a transient/network
 * failure. See `computeWorklistAccessStatus`'s `ipa-forbidden` vs `ipa-error`.
 */
export type IpaFetchState = "idle" | "loading" | "loaded" | "error" | "forbidden";

/** Pure status derivation — unit-testable without mounting any React tree. */
export function computeWorklistAccessStatus(
  hasPpBearer: boolean,
  npi: string | null,
  ipaFetch: IpaFetchState,
  ipaCount: number,
): WorklistAccessStatus {
  if (!hasPpBearer) return "no-pp-session";
  // The IPA must resolve BEFORE the NPI is asked for: the entitled NPI(s)
  // arrive WITH the IPA response (EntitledIpaEntry.providerNpis), so
  // prompting for an NPI before the IPA is known would ask for something
  // we're about to receive from the very same call. `ipaCount === 0` is
  // checked first (same as before) so stale-while-revalidate — once we have
  // IPAs in hand, keep the worklist (and its always-available IPA switcher)
  // up even while a bearer-change re-fetch is in flight or a transient
  // re-fetch just failed — never tear the surface down to a full-screen
  // spinner/error we'd have to climb back out of.
  if (ipaCount === 0) {
    // Nothing to show yet: first-load spinner, a hard first-load error/
    // denial, or a genuinely empty entitled-IPA result. `forbidden` (403) is
    // checked BEFORE the generic `error` — it's a fail-closed backend denial,
    // never a retryable transient failure, and must render its own distinct
    // (no Try-again) state.
    if (ipaFetch === "loading" || ipaFetch === "idle") return "resolving-ipa";
    if (ipaFetch === "forbidden") return "ipa-forbidden";
    if (ipaFetch === "error") return "ipa-error";
    return "no-ipa";
  }
  // The IPA is resolved from here on: with >=1 entitled IPA, `resolvedIpaId`
  // always lands on one (ACT-1431 — the pick is auto-resolved, never asked
  // for), so there is no "IPAs in hand but none chosen" state left to gate on.
  // Likewise for the NPI: >=1 entitled NPI always resolves. `no-npi` therefore
  // means what it says — ZERO entitled NPIs and nothing manually entered yet.
  if (!npi) return "no-npi";
  return "ready";
}

export type WorklistNpiSource = "auto" | "stored" | "none";

export interface ResolvedWorklistNpi {
  npi: string | null;
  source: WorklistNpiSource;
}

/**
 * Resolves the NPI to use for the worklist from the entitled-NPI list PP-API
 * returned alongside the CURRENTLY RESOLVED IPA (see fetchEntitledIpas.ts /
 * EntitledIpaEntry.providerNpis). Pure — unit-testable without mounting any
 * React tree, same style as `computeWorklistAccessStatus`.
 *
 * THE SERVER WINS whenever it can: exactly one entitled NPI is auto-linked
 * unconditionally, ignoring any stored value (a stale stored pick from a
 * previous session must never override a fresh, unambiguous server answer).
 * More than one entitled NPI reuses a previously-stored pick if it's still one
 * of the CURRENT entitled NPIs (permission grants can change server-side), and
 * otherwise falls through to the FIRST entitled NPI rather than blocking on a
 * picker (ACT-1431 — Provider Portal auto-picks the first/persisted provider
 * too; see `QualityGap/ProviderSelect.tsx`). The provider can still change it
 * from the identity bar's switcher, which is the whole point of resolving
 * rather than asking. Zero entitled NPIs preserves the original manual
 * free-entry fallback (stored-or-null).
 */
export function resolveWorklistNpi(
  entitledNpis: string[],
  storedNpi: string | null,
): ResolvedWorklistNpi {
  if (entitledNpis.length === 1) {
    return { npi: entitledNpis[0], source: "auto" };
  }
  if (entitledNpis.length > 1) {
    if (storedNpi && entitledNpis.includes(storedNpi)) {
      return { npi: storedNpi, source: "stored" };
    }
    return { npi: entitledNpis[0], source: "auto" };
  }
  return { npi: storedNpi, source: storedNpi ? "stored" : "none" };
}

export interface UseWorklistAccessGateResult {
  status: WorklistAccessStatus;
  /** Email of whoever is currently signed in, or undefined if unresolvable. */
  identityEmail: string | undefined;
  /** Drives the shared PpLoginModal — true while a login prompt is pending. */
  ppLoginOpen: boolean;
  /** Opens the seamless PP login modal (idempotent — safe to call repeatedly). */
  requestPpLogin: () => void;
  cancelPpLogin: () => void;
  handlePpLoginSuccess: () => void;
  /** The resolved NPI for the current identity + IPA, or null if not yet resolved. */
  npi: string | null;
  /** Whether `npi` came from the server's single-entitled-NPI auto-link, a
   *  persisted explicit pick/manual entry, or hasn't resolved at all yet. */
  npiSource: WorklistNpiSource;
  npiError: string | null;
  /**
   * Validates + persists an entered NPI (manual entry — used when the
   * provider has ZERO entitled NPIs for the resolved IPA). Returns false
   * (and sets npiError) if invalid, or if the entitled list is non-empty and
   * the value isn't one of the entitled NPIs.
   */
  submitNpi: (value: string) => boolean;
  /** "Change NPI" affordance for the ZERO-entitled-NPI manual-entry case —
   *  clears the persisted value, returning the gate to `no-npi`. With entitled
   *  NPIs in hand the switcher (`selectNpi`) is the affordance instead, since
   *  clearing would just re-resolve to the first entitled NPI. */
  clearNpi: () => void;
  /** Every entitled NPI for the CURRENTLY RESOLVED IPA. Empty means the
   *  provider has no entitled NPI under this IPA at all — the manual-entry
   *  path (`no-npi` / `submitNpi`). Drives the identity bar's NPI switcher
   *  when it holds more than one. */
  entitledNpis: string[];
  /** Switches to a different entitled NPI (from the identity bar's switcher,
   *  when entitledNpis.length > 1) and persists it per-device for this
   *  identity + IPA. */
  selectNpi: (npi: string) => void;
  /** Every IPA the signed-in provider is entitled to, from PP-API (deduped, may be empty). */
  ipas: string[];
  /** Re-runs the PP-API IPA fetch — drives the "Try again" affordance on the error state. */
  retryIpas: () => void;
  /**
   * The IPA to actually use for the worklist's `companyId`/`ipaId` props: the
   * persisted/chosen value when it's still one of `ipas`, else `ipas[0]`, or
   * `null` only when there are ZERO entitled IPAs (see `status`'s `no-ipa`).
   * Never null while `ipas` is non-empty — ACT-1431 auto-resolves the pick.
   */
  resolvedIpaId: string | null;
  /** Switches the worklist to a different entitled IPA and persists the pick
   *  per-device for this identity. */
  selectIpa: (ipa: string) => void;
  /** Clears the persisted IPA selection, reverting to the auto-resolved default
   *  (`ipas[0]`). Kept for the identity-reset paths; the switcher is what a
   *  provider uses to change IPA. */
  clearSelectedIpa: () => void;
}

export type UseWorklistAccessGateOptions = {
  /**
   * When false, skip `POST ProviderOverview/EntitledIpas` and treat a PP
   * bearer as sufficient for `ready`. Used by the Desktop-parity Patient
   * Panel (`providerRosterLazy`): IPA/NPI come from the in-module provider
   * picker + local storage, not this entitlement probe. Default true so Home
   * cards / AdtBell keep auto-linking from EntitledIpas.
   */
  fetchEntitlements?: boolean;
};

export function useWorklistAccessGate(
  options: UseWorklistAccessGateOptions = {},
): UseWorklistAccessGateResult {
  const fetchEntitlements = options.fetchEntitlements !== false;
  const { selectedProvider, accessToken, accountLabel } = useAuthContext();
  const identityEmail = resolveIdentityEmail(accountLabel);
  const oid = jwtUserOid(accessToken) ?? undefined;
  const ppApiBaseUrl = `${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`;

  // Re-render on ANY mutation of the PP token stash, whoever caused it — a
  // silent `ensurePpAccessToken` refresh, a `PpLoginModal` success, a logout.
  // `getPpToken`/`getStoredNpi` read live localStorage on every render, so a
  // re-render is all that's needed to re-derive them.
  //
  // SHARED, not a private counter (which is what this used to be). This hook
  // is mounted by several INDEPENDENT call sites at once — on Home that is
  // `HomePpConnectPrompt`, `UrgentPatientsWidget` and
  // `HospitalEdEventsWidget` — and a private counter only ever re-rendered
  // the instance whose own handler fired. So a PP login through the Home
  // prompt flipped the PROMPT to `"ready"` (correctly hiding it) while both
  // widgets' instances kept the `ppToken === undefined` they had derived
  // before the login and stayed on `"no-pp-session"`, rendering nothing:
  // Home went blank after sign-in until a full page reload. Subscribing to
  // the stash itself (see b2cClient.ts's `subscribeStoredTokens`) makes every
  // instance re-derive from the same event.
  useSyncExternalStore(subscribeStoredTokens, getStoredTokensVersion, getStoredTokensVersion);

  const [ppLoginOpen, setPpLoginOpen] = useState(false);
  const gateRef = useRef<PpLoginGate | null>(null);
  if (!gateRef.current) {
    gateRef.current = new PpLoginGate({ onOpen: () => setPpLoginOpen(true) });
  }

  const ppToken = getPpToken(selectedProvider, accessToken, identityEmail);

  // Mirrors MemberProfilePage's ensurePpSession silent-first step (ACT-1247
  // SSO-PP auth-linking Phase 1, Option A): if there's no LIVE bearer yet but
  // a PP link is persisted for this identity, try a silent refresh before
  // ever prompting for credentials.
  useEffect(() => {
    if (ppToken) return;
    if (!identityEmail || !hasPersistedPpLink(identityEmail)) return;
    // Fire-and-forget, and no cleanup flag any more: a refresh that mints a
    // new token persists it through `setStoredTokens`, which notifies every
    // subscriber on its own, so this no longer calls `setState` in a `.then`
    // and has nothing to cancel on unmount.
    void ensurePpAccessToken(identityEmail);
  }, [identityEmail, ppToken]);

  const requestPpLogin = useCallback(() => {
    void gateRef.current?.request().catch(() => {
      /* cancelled — status simply stays "no-pp-session" */
    });
  }, []);

  const cancelPpLogin = useCallback(() => {
    setPpLoginOpen(false);
    gateRef.current?.cancel();
  }, []);

  const handlePpLoginSuccess = useCallback(() => {
    setPpLoginOpen(false);
    gateRef.current?.succeed();
    // `ropcLogin` already wrote the stash through `setStoredTokens`, which
    // notified every subscriber — including the OTHER call sites of this hook,
    // which is the point (see the `useSyncExternalStore` note above).
  }, []);

  // IPA precondition + entitled-NPI source. The IPA/NPI
  // set is NOT in AstraConnect's own auth payload (care-management-gql leaves
  // permissionMeta.ipas undefined and member_360 CONFIG stores no per-user
  // IPA/NPI), so it's fetched from the provider's PP identity via PP-API,
  // gated behind the same PP bearer as the rest of the worklist. Fires once
  // the bearer is present; re-fires on a bearer change (e.g. a re-auth/
  // refresh surfaces updated grants) or an explicit retry.
  //
  // Skipped when `fetchEntitlements: false` (lazy Patient Panel): the picker
  // owns provider/IPA scope; this probe would only duplicate Desktop's unused
  // EntitledIpas call on that surface.
  const [ipaEntries, setIpaEntries] = useState<EntitledIpaEntry[]>([]);
  const [ipaFetch, setIpaFetch] = useState<IpaFetchState>("idle");
  const [ipaReloadTick, setIpaReloadTick] = useState(0);

  useEffect(() => {
    if (!fetchEntitlements) {
      setIpaEntries([]);
      setIpaFetch("loaded");
      return;
    }
    if (!ppToken) {
      setIpaEntries([]);
      setIpaFetch("idle");
      return;
    }
    let cancelled = false;
    setIpaFetch("loading");
    void fetchEntitledIpas(ppApiBaseUrl, ppToken)
      .then((entries) => {
        if (cancelled) return;
        setIpaEntries(entries);
        setIpaFetch("loaded");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Keep the last-good IPAs (stale-while-revalidate) so a transient
        // re-fetch failure never blanks a worklist that's already up; only a
        // FIRST-load failure (ipas still empty) surfaces the error/denial state.
        // A 403 is a fail-closed BACKEND DENIAL (e.g. a "view as" peek that
        // isn't entitled to a worklist) — distinct from a retryable transient
        // failure, so it gets its own `ipaFetch` state (see IpaFetchState's doc).
        if (err instanceof EntitledIpasError && err.status === 403) {
          // Unlike a transient failure (left as-is below, stale-while-
          // revalidate), a 403 is a DEFINITIVE backend denial — a
          // mid-session revocation must actually surface, not hide behind
          // the last-good `ipas` list until a reload (grill MINOR fix).
          setIpaEntries([]);
          setIpaFetch("forbidden");
          return;
        }
        setIpaFetch("error");
      });
    return () => {
      cancelled = true;
    };
  }, [fetchEntitlements, ppToken, ppApiBaseUrl, ipaReloadTick]);

  const retryIpas = useCallback(() => setIpaReloadTick((t) => t + 1), []);

  // Public `ipas: string[]` shape is unchanged by the entries-instead-of-
  // strings switch above — memoized so switcher consumers do not churn on every
  // render.
  const ipas = useMemo(() => ipaEntries.map((e) => e.ipa), [ipaEntries]);

  const [selectedIpa, setSelectedIpa] = useState<string | null>(() => getStoredIpa(oid));
  // Re-sync whenever the signed-in identity changes, same reasoning as npi's
  // resync below — a different provider on this device must never inherit a
  // stale IPA choice.
  useEffect(() => {
    setSelectedIpa(getStoredIpa(oid));
  }, [oid]);

  // A persisted/chosen IPA only counts if it's still one of the provider's
  // CURRENT ipas — permission grants can change server-side after the value
  // was stored. When entitlements are not fetched (lazy Patient Panel), the
  // stored/picked IPA is authoritative — there is no server list to check.
  const validSelectedIpa =
    !fetchEntitlements
      ? selectedIpa
      : selectedIpa && ipas.includes(selectedIpa)
        ? selectedIpa
        : null;
  // ACT-1431: auto-resolve rather than gate. The provider's own pick wins when
  // it's still entitled; otherwise fall through to the first entitled IPA —
  // including for a multi-IPA provider, who used to be stopped by a full-page
  // picker here. Mirrors PP's `resolveIpa` (last-used → first entitlement, each
  // candidate entitlement-checked). A revoked stored IPA therefore degrades to
  // a different entitled IPA instead of blocking, and is never sent on the wire.
  const resolvedIpaId = validSelectedIpa ?? ipas[0] ?? null;

  const selectIpa = useCallback(
    (ipa: string) => {
      if (oid) setStoredIpa(oid, ipa);
      setSelectedIpa(ipa);
    },
    [oid],
  );

  const clearSelectedIpa = useCallback(() => {
    if (oid) clearStoredIpa(oid);
    setSelectedIpa(null);
  }, [oid]);

  // Entitled NPI(s) for the CURRENTLY RESOLVED IPA — arrives WITH the IPA
  // response, so this is `[]` until an IPA has actually resolved (see
  // computeWorklistAccessStatus's ipa-before-npi ordering).
  const entitledNpis = ipaEntries.find((e) => e.ipa === resolvedIpaId)?.providerNpis ?? [];

  // Identity of the (provider, IPA) pair the NPI below belongs to. Changes in
  // the SAME render as `resolvedIpaId`.
  const npiKey = `${oid ?? ""}:${resolvedIpaId ?? ""}`;

  // The user's just-made explicit choice, tagged with the pair it was made
  // for. Also the only home for the value when there's no `oid` to key storage
  // by (preserves the pre-existing session-only behavior in that edge case).
  const [pendingNpi, setPendingNpi] = useState<{ key: string; npi: string | null } | null>(null);

  // Derived SYNCHRONOUSLY — deliberately NOT state+effect. `resolvedIpaId`
  // updates in the same render as `selectIpa`, so an effect-based resync would
  // lag it by one commit and pair the OLD IPA's NPI with the NEW IPA's `ipaId`
  // for that frame. The worklist would then send a mismatched (npi, ipa) pair,
  // which the backend validates per-pair and rejects fail-closed with a 403.
  // The `key` guard is what makes this safe: a pending choice is honored only
  // while it still belongs to the current pair, so switching IPA falls straight
  // through to that IPA's own stored value with no stale frame in between.
  const storedNpi = useMemo(
    () => (pendingNpi?.key === npiKey ? pendingNpi.npi : getStoredNpi(oid, resolvedIpaId)),
    [pendingNpi, npiKey, oid, resolvedIpaId],
  );

  const [npiError, setNpiError] = useState<string | null>(null);

  const { npi, source: npiSource } = resolveWorklistNpi(entitledNpis, storedNpi);

  const submitNpi = useCallback(
    (value: string): boolean => {
      const trimmed = value.trim();
      if (!isValidNpi(trimmed)) {
        setNpiError("Enter a valid 10-digit NPI.");
        return false;
      }
      // DEFENSIVE ONLY — unreachable through the shipped UI. `NpiRequiredState`
      // (the sole caller) renders only in `no-npi`, which per
      // computeWorklistAccessStatus occurs only when `entitledNpis` is EMPTY;
      // a non-empty list always resolves to `ready`. Kept as a guard for
      // direct/programmatic callers so a mismatched (npi, ipa) pair can never
      // be persisted if that ordering is ever changed.
      if (entitledNpis.length > 0 && !entitledNpis.includes(trimmed)) {
        setNpiError("That NPI isn't one of your entitled provider NPIs for this IPA.");
        return false;
      }
      setNpiError(null);
      if (oid && resolvedIpaId) setStoredNpi(oid, resolvedIpaId, trimmed);
      setPendingNpi({ key: npiKey, npi: trimmed });
      return true;
    },
    [oid, resolvedIpaId, entitledNpis, npiKey],
  );

  const selectNpi = useCallback(
    (value: string) => {
      if (oid && resolvedIpaId) setStoredNpi(oid, resolvedIpaId, value);
      setPendingNpi({ key: npiKey, npi: value });
      setNpiError(null);
    },
    [oid, resolvedIpaId, npiKey],
  );

  const clearNpi = useCallback(() => {
    if (oid && resolvedIpaId) clearStoredNpi(oid, resolvedIpaId);
    setPendingNpi({ key: npiKey, npi: null });
    setNpiError(null);
  }, [oid, resolvedIpaId, npiKey]);

  // Lazy Patient Panel: PP bearer alone is enough to mount the picker; empty
  // NPI/IPA is a valid "select a provider" seed (see WorklistPage).
  const status: WorklistAccessStatus = !fetchEntitlements
    ? ppToken
      ? "ready"
      : "no-pp-session"
    : computeWorklistAccessStatus(!!ppToken, npi, ipaFetch, ipas.length);

  return {
    status,
    identityEmail,
    ppLoginOpen,
    requestPpLogin,
    cancelPpLogin,
    handlePpLoginSuccess,
    npi,
    npiSource,
    npiError,
    submitNpi,
    clearNpi,
    entitledNpis,
    selectNpi,
    ipas,
    retryIpas,
    resolvedIpaId,
    selectIpa,
    clearSelectedIpa,
  };
}
